import { Injectable, NgZone } from '@angular/core';

import { EditMode } from '../map/map-editing';
import { displayName } from '../map/suburb-map.engine';
import { Finding } from '../rules/suburb-map-checks';
import { SubRegionAction } from '../rules/suburb-subregion-edits';
import { CheckReport, checkReport } from '../tools/check-report';
import { SuburbCatalogueService } from './catalogue.service';
import { PanelNavService } from './panel-nav.service';
import { SuburbDetailService } from './suburb-detail.service';
import { WorkspaceStateService } from './workspace-state.service';

/**
 * The map's commands (#256, #260), from the tool column, the keyboard and the panels: change state, pick a region or
 * sub-region, Grow, the checks, the search, and the panels' one-suburb edits (put in a region, take out, join or leave
 * a sub-region). Each map edit goes through the engine, which applies it and hands it to MapEditsService to save.
 */
@Injectable()
export class MapToolsService {
  /** The last run of the checks, and their findings (Fill enclaves fills from them). */
  checks: CheckReport | null = null;
  private findings: Finding[] = [];

  constructor(
    private zone: NgZone,
    private state: WorkspaceStateService,
    private catalogue: SuburbCatalogueService,
    private nav: PanelNavService,
    private detail: SuburbDetailService
  ) {}

  async changeState(code: string): Promise<void> {
    const state = this.state;
    state.stateCode = code;
    await this.zone.runOutsideAngular(() => state.engine.loadState(code));
    state.engine.syncToolCursor();
    state.redraw();
  }

  grow(): void {
    this.state.engine.grow().catch((e) => this.state.setStatus(e.message, true));
  }

  /** Keys 1–9: that region becomes the active one. */
  pickRegion(index: number): void {
    const region = this.catalogue.regions[index];
    this.state.activeRegion = region.id;
    this.state.setStatus(`Active region: ${region.name}`);
  }

  /** The Regions tally and a sub-region's panel: make it the one Sub-region mode edits. */
  editSubRegionOnMap(id: string): void {
    this.state.mode = 'subregion';
    this.state.activeSubRegion = id;
    this.state.setStatus(
      `Active sub-region: ${this.catalogue.subRegionName(id)}. A click puts a suburb in or takes it out; Add / Remove (X) is for paint and lasso.`
    );
  }

  toggleSubRegionAction(): void {
    const state = this.state;
    state.subRegionAction = state.subRegionAction === 'add' ? 'remove' : 'add';
    const what = state.subRegionAction === 'add' ? 'add suburbs to' : 'take suburbs out of';
    state.setStatus(
      `Sub-region sweeps now ${what} ${this.catalogue.subRegionName(state.activeSubRegion) || 'the active sub-region'}.`
    );
  }

  /** A search result: select it and open its panel. */
  pickResult(code: string): void {
    this.state.engine.select(code);
    this.nav.openSuburb(code);
  }

  async runChecks(): Promise<void> {
    const state = this.state;
    state.setStatus('Running checks…');
    const { findings, subRegionFindings, graph } = await state.engine.checks();
    this.findings = findings;
    this.checks = checkReport(findings, subRegionFindings, graph, {
      region: (id) => this.catalogue.regionName(id),
      subRegion: (id) => this.catalogue.subRegionName(id),
      regionState: (id) => state.engine.regionById(id)?.state,
    });
    state.setStatus('Checks done.');
    state.redraw();
  }

  /** Fill each enclave its neighbours all agree on, then check again. */
  fillEnclaves(): void {
    const n = this.state.engine.fillEnclaves(this.findings);
    if (n) this.state.setStatus(`Filled ${n} enclave${n === 1 ? '' : 's'}. Cmd+Z undoes.`);
    void this.runChecks();
  }

  /** The Overview's buttons: one suburb, one change, saved and undoable like a map click. */
  quick(mode: EditMode): void {
    const code = this.state.selectedCode;
    if (!code) return;
    this.state.engine.editOne(
      code,
      mode,
      mode === 'assign' ? `put in ${this.catalogue.regionName(this.state.activeRegion)}` : mode
    );
  }

  /** One suburb into or out of one sub-region, from the panels: one save, one undo step. */
  membership(code: string, subRegionId: string, action: SubRegionAction): void {
    if (this.state.readOnly) return;
    const sr = this.catalogue.subRegionName(subRegionId);
    const name = displayName(this.state.engine.areaName(code) || this.detail.name) || code;
    // Nothing to save (another region, say) leaves the engine's status saying why.
    this.state.engine.editMembership(
      code,
      subRegionId,
      action,
      action === 'add' ? `put ${name} in ${sr}` : `take ${name} out of ${sr}`
    );
  }
}
